import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FaCreditCard,
  FaMapMarkerAlt,
  FaCartPlus,
  FaBoxOpen,
  FaTags,
  FaWallet,
  FaTruck,
  FaTimes,
  FaMoneyBillWave,
  FaEdit,
  FaSave,
  FaGoogleWallet,
  FaGooglePay,
  FaCcVisa,
  FaMobileAlt,
} from "react-icons/fa";
import Navbar from "./Navbar";
import Fotter from "./Fotter";
import axios from "axios";

// Import BASE_URL from your backend configuration
import { BASE_URL } from "../backend/backend.js";

const formatPrice = (price) => {
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
  }).format(price);
};

const OrderSummary = () => {
  const [cartItems, setCartItems] = useState([]);
  const [isCheckoutModalOpen, setIsCheckoutModalOpen] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState("UPI");
  const [address, setAddress] = useState("");
  const [isEditingAddress, setIsEditingAddress] = useState(false);
  const [tempAddress, setTempAddress] = useState("");
  const [location, setLocation] = useState({ lat: null, lon: null });

  // Fetch Cart Items
  useEffect(() => {
    axios
      // Replaced hardcoded URL with BASE_URL
      .get(`${BASE_URL}/user/getAllCartProduct`, {
        withCredentials: true,
      })
      .then((res) => {
        if (res.data?.body) {
          setCartItems(res.data.body);
        }
      })
      .catch((err) => {
        console.error("Error fetching cart:", err);
      });
  }, []);

  // Fetch Geolocation and Address
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const { latitude, longitude } = position.coords;
          setLocation({ lat: latitude, lon: longitude });

          try {
            // Replaced hardcoded URL with BASE_URL
            const response = await axios.post(
              `${BASE_URL}/api/saveLocation`,
              { latitude, longitude },
              { withCredentials: true }
            );
            if (typeof response.data === "string") {
              setAddress(response.data);
              setTempAddress(response.data);
            }
          } catch (error) {
            console.error("Error saving location:", error);
          }
        },
        (error) => {
          console.error("Error getting location:", error);
        }
      );
    }
  }, []);

  const originalTotalPrice = cartItems.reduce((acc, item) => acc + item.medicine.price * item.quantity, 0);
  const discountedTotalPrice = cartItems.reduce(
    (total, item) => total + item.medicine.descountPrice * item.quantity,
    0
  );
  const discountPercentage =
    originalTotalPrice > 0 ? ((originalTotalPrice - discountedTotalPrice) / originalTotalPrice) * 100 : 0;

  const otherCharges = 50;
  const finalTotal = discountedTotalPrice + otherCharges;

  const handleSubmitPayment = () => {
    let paymentDetails = {};
    if (paymentMethod === "Credit/Debit Card") {
      const cardNumber = document.getElementById("cardNumber").value;
      const expiryDate = document.getElementById("expiryDate").value;
      const cardCVV = document.getElementById("cardCVV").value;

      if (!cardNumber || !expiryDate || !cardCVV) {
        alert("Please fill in all card details.");
        return;
      }
      paymentDetails = { cardNumber, expiryDate, cvv: cardCVV };
      console.log("Processing card:", cardNumber);
    }

    if (paymentMethod === "UPI") {
      const upiId = document.getElementById("upiId").value;
      if (!upiId) {
        alert("Please enter UPI ID.");
        return;
      }
      paymentDetails = { upiId };
      console.log("Processing UPI:", upiId);
    }

    const requestBody = {
      paymentMode: paymentMethod,
      subTotal: discountedTotalPrice,
      discount: originalTotalPrice - discountedTotalPrice,
      otherCharge: otherCharges,
      totalPrice: finalTotal,
      fullAddress: address, // Sending the address here
      paymentDetails: paymentDetails, // Include payment details if needed by your backend
    };

    axios
      // Replaced hardcoded URL with BASE_URL
      .post(`${BASE_URL}/user/placeOrder`, requestBody, {
        withCredentials: true,
      })
      .then((response) => {
        console.log("Order placed successfully:", response.data);
        alert("Payment Successful! Your order has been placed.");
        setIsCheckoutModalOpen(false);
        setPaymentMethod("");
        // Optionally, redirect the user or clear the cart
      })
      .catch((error) => {
        console.error("Error placing order:", error);
        alert("Payment failed. Please try again.");
      });
  };

  const handleSaveAddress = () => {
    setAddress(tempAddress);
    setIsEditingAddress(false);
  };

  return (
    <>
      <Navbar />
      <div style={{ paddingTop: "6rem" }} />
      <motion.div
        className="summary-container"
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
          padding: "2rem",
          background: "linear-gradient(to right, #f5f7fa, #c3cfe2)",
          fontFamily: "Poppins, sans-serif",
        }}
      >
        <motion.div
          className="glass-card"
          initial={{ scale: 0.9 }}
          animate={{ scale: 1 }}
          transition={{ duration: 0.4 }}
          style={{
            background: "rgba(255, 255, 255, 0.85)",
            backdropFilter: "blur(10px)",
            borderRadius: "20px",
            boxShadow: "0 8px 24px rgba(0,0,0,0.2)",
            padding: "2rem",
            width: "90%",
            maxWidth: "800px",
          }}
        >
          <motion.h2
            style={{
              fontSize: "2rem",
              marginBottom: "1.5rem",
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
              color: "#2c3e50",
            }}
            whileHover={{ scale: 1.05 }}
          >
            <FaCartPlus /> Order Summary
          </motion.h2>

          {cartItems.map((item, index) => (
            <motion.div
              key={index}
              whileHover={{ scale: 1.01 }}
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                background: "#f8f9fa",
                padding: "1rem",
                borderRadius: "12px",
                marginBottom: "0.8rem",
                boxShadow: "0 2px 5px rgba(0,0,0,0.1)",
              }}
            >
              <div>
                <strong>{item.medicine.medicineName}</strong>
                <br />
                Qty: {item.quantity} | <span style={{ textDecoration: 'line-through', color: 'grey' }}>{formatPrice(item.medicine.price)}</span> {formatPrice(item.medicine.descountPrice)}
              </div>
              <div>
                <span style={{ fontWeight: "bold", color: "#27ae60" }}>
                  {formatPrice(item.medicine.descountPrice * item.quantity)}
                </span>
                {item.medicine.price > item.medicine.descountPrice && (
                  <span style={{ color: 'orange', fontSize: '0.9rem', marginLeft: '0.5rem' }}>
                    ({formatPrice(item.medicine.price * item.quantity - item.medicine.descountPrice * item.quantity)} off)
                  </span>
                )}
              </div>
            </motion.div>
          ))}
          <hr style={{ margin: "1.5rem 0" }} />

          <div
            style={{
              background: "#ecf0f1",
              padding: "1rem",
              borderRadius: "12px",
              marginBottom: "1rem",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              gap: "1rem",
            }}
          >
            <div style={{ flex: 1 }}>
              <strong>
                <FaMapMarkerAlt /> Delivery Address:
              </strong>
              {isEditingAddress ? (
                <textarea
                  value={tempAddress}
                  onChange={(e) => setTempAddress(e.target.value)}
                  rows={3}
                  style={{
                    width: "100%",
                    marginTop: "0.5rem",
                    padding: "0.5rem",
                    borderRadius: "8px",
                    border: "1px solid #bdc3c7",
                    fontFamily: "inherit",
                  }}
                />
              ) : (
                <p style={{ marginTop: "0.5rem", marginLeft: "1.5rem" }}>
                  {address || "Fetching address..."}
                </p>
              )}
            </div>
            {isEditingAddress ? (
              <FaSave
                onClick={handleSaveAddress}
                style={{
                  cursor: "pointer",
                  fontSize: "1.4rem",
                  color: "#27ae60",
                  marginTop: "0.3rem",
                }}
                title="Save Address"
              />
            ) : (
              <FaEdit
                onClick={() => {
                  setTempAddress(address);
                  setIsEditingAddress(true);
                }}
                style={{
                  cursor: "pointer",
                  fontSize: "1.2rem",
                  color: "#2980b9",
                }}
                title="Edit Address"
              />
            )}
          </div>

          <div style={{ textAlign: "right", lineHeight: "1.8" }}>
            <p>
              <FaTags /> Total: {formatPrice(originalTotalPrice)}
            </p>
            <p>
              <FaTags /> Discount: {formatPrice(originalTotalPrice - discountedTotalPrice)}{" "}
              ({discountPercentage.toFixed(2)}%)
            </p>
            <p>
              <FaTags /> Subtotal: {formatPrice(discountedTotalPrice)}
            </p>
            <p>
              <FaTruck /> Other Charges: {formatPrice(otherCharges)}
            </p>
            <h3 style={{ color: "#e67e22" }}>
              <FaWallet /> Final Total: {formatPrice(finalTotal)}
            </h3>
          </div>

          <motion.button
            whileHover={{ scale: 1.05 }}
            onClick={() => setIsCheckoutModalOpen(true)}
            style={{
              marginTop: "2rem",
              background: "#3498db",
              color: "#fff",
              padding: "1rem 2rem",
              fontSize: "1.1rem",
              border: "none",
              borderRadius: "12px",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
            }}
          >
            <FaCreditCard /> Proceed to Checkout
          </motion.button>
        </motion.div>
      </motion.div>

      {/* Checkout Modal */}
      <AnimatePresence>
        {isCheckoutModalOpen && (
          <motion.div
            className="modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              width: "100vw",
              height: "100vh",
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 1000,
            }}
          >
            <motion.div
              className="modal-content"
              initial={{ y: "-100vh" }}
              animate={{ y: 0 }}
              exit={{ y: "-100vh" }}
              style={{
                background: "#fff",
                padding: "1.5rem",
                borderRadius: "20px",
                width: "90%",
                maxWidth: "600px",
                position: "relative",
                boxShadow: "0 10px 30px rgba(0,0,0,0.25)",
                maxHeight: "80vh",
                overflowY: "auto",
              }}
            >
              {/* Close Button */}
              <button
                onClick={() => setIsCheckoutModalOpen(false)}
                style={{
                  position: "absolute",
                  top: "15px",
                  right: "15px",
                  background: "#f8f8f8",
                  border: "none",
                  borderRadius: "50%",
                  width: "35px",
                  height: "35px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "1.2rem",
                  cursor: "pointer",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                }}
                title="Close"
              >
                <FaTimes color="#333" />
              </button>

              <h2 style={{ marginBottom: "1rem", fontSize: "1.2rem" }}>
                <FaBoxOpen /> Checkout Details
              </h2>

              <p style={{ fontSize: "0.9rem" }}>
                <FaMapMarkerAlt /> <strong>Delivery Address:</strong> {address}
              </p>

              <h4 style={{ marginTop: "1rem", fontSize: "1rem" }}>
                <FaTags /> Order Summary:
              </h4>

              {cartItems.map((item, idx) => (
                <div
                  key={idx}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    marginBottom: "0.5rem",
                    fontSize: "0.9rem",
                  }}
                >
                  <span>
                    {item.medicine.medicineName} × {item.quantity}
                  </span>
                  <span>{formatPrice(item.medicine.descountPrice * item.quantity)}</span>
                </div>
              ))}

              <div style={{ marginTop: "1rem", borderTop: "1px solid #ccc", paddingTop: "1rem" }}>
                <h4 style={{ fontSize: "1rem" }}><FaCreditCard /> Payment Method</h4>
                <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                  {[
                    { label: "UPI", icon: <FaGooglePay /> },
                    { label: "Credit/Debit Card", icon: <FaCcVisa /> },
                    { label: "Wallet", icon: <FaGoogleWallet /> },
                    { label: "Cash on Delivery", icon: <FaMoneyBillWave /> },
                  ].map((method) => (
                    <label
                      key={method.label}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "0.6rem",
                        cursor: "pointer",
                        background: "#f1f1f1",
                        padding: "0.6rem 1rem",
                        borderRadius: "10px",
                        fontWeight: paymentMethod === method.label ? "bold" : "normal",
                        border: paymentMethod === method.label ? "2px solid #3498db" : "1px solid #ccc",
                        fontSize: "0.9rem",
                      }}
                    >
                      <input
                        type="radio"
                        value={method.label}
                        checked={paymentMethod === method.label}
                        onChange={(e) => setPaymentMethod(e.target.value)}
                        style={{ accentColor: "#3498db" }}
                      />
                      {method.icon}
                      {method.label}
                    </label>
                  ))}
                </div>

                {paymentMethod === "Credit/Debit Card" && (
                  <div style={{ marginTop: "1rem" }}>
                    <h5 style={{ fontSize: "1rem" }}>Enter Card Details</h5>
                    <form>
                      <div style={{ marginBottom: "1rem" }}>
                        <label htmlFor="cardNumber">Card Number</label>
                        <input
                          type="text"
                          id="cardNumber"
                          placeholder="XXXX XXXX XXXX XXXX"
                          style={inputStyle}
                          required
                        />
                      </div>

                      <div style={{ marginBottom: "1rem" }}>
                        <label htmlFor="expiryDate">Expiry Date</label>
                        <input
                          type="text"id="expiryDate"
                          placeholder="MM/YY"
                          style={inputStyle}
                          required
                        />
                      </div>

                      <div style={{ marginBottom: "1rem" }}>
                        <label htmlFor="cardCVV">CVV</label>
                        <input
                          type="text"
                          id="cardCVV"
                          placeholder="CVV"
                          style={inputStyle}
                          required
                        />
                      </div>
                    </form>
                  </div>
                )}

                {paymentMethod === "UPI" && (
                  <div style={{ marginTop: "1rem" }}>
                    <label htmlFor="upiId">Enter UPI ID</label>
                    <input
                      type="text"
                      id="upiId"
                      placeholder="example@upi"
                      style={inputStyle}
                      required
                    />
                  </div>
                )}
              </div>

              <div style={{ textAlign: "right", marginTop: "1.5rem", fontSize: "0.9rem" }}>
                <p>Subtotal: {formatPrice(discountedTotalPrice)}</p>
                <p>Discount: {formatPrice(originalTotalPrice - discountedTotalPrice)}</p>
                <p>Other Charges: {formatPrice(otherCharges)}</p>
                <h3>Total: {formatPrice(finalTotal)}</h3>
              </div>

              <div style={{ textAlign: "center", marginTop: "1.5rem" }}>
                <button
                  onClick={handleSubmitPayment}
                  style={{
                    backgroundColor: "#3498db",
                    color: "#fff",
                    border: "none",
                    padding: "1rem 2rem",
                    borderRadius: "10px",
                    cursor: "pointer",
                    fontSize: "1rem",
                  }}
                >
                  Submit Payment
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <Fotter />
    </>
  );
};
const inputStyle = {
  width: "100%",
  padding: "0.8rem",
  marginTop: "0.5rem",
  borderRadius: "8px",
  border: "1px solid #bdc3c7",
};
export default OrderSummary;